import React, { useCallback } from 'react'
import { useFieldArray, Control } from 'react-hook-form'
import { DropDownPicker, TextInput } from '@/components'
import { InstallationTypeEnum, type InstallationRequest } from '@/generated'
import { t } from '@/i18n'
import styles from './InstallationsModal.module.scss'

interface Props {
  control: Control<any>
  isEditing?: boolean
  emptyValue?: string | null
  onClose: () => void
}

const InstallationsModal: React.FC<Props> = ({
  control,
  isEditing = true,
  emptyValue,
  onClose,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'installations',
  })

  const installationTypeOptions = [
    {
      value: InstallationTypeEnum.LEASING_COMPANY,
      label: t('leasing_company'),
    },
    { value: InstallationTypeEnum.IMPORTER, label: t('importer') },
  ]

  const addInstallation = useCallback(() => {
    const newInstallation: InstallationRequest = {
      name: '',
      price_without_vat: '0',
      installation_type: InstallationTypeEnum.LEASING_COMPANY,
      is_customer_paid: false,
    }
    append(newInstallation)
  }, [append])

  const removeInstallation = useCallback(
    (index: number) => {
      remove(index)
    },
    [remove],
  )

  const rest = { isEditing, disabled: !isEditing, emptyValue }

  return (
    <div className={styles.installationsModal}>
      <div className={styles.modalHeader}>
        {isEditing && (
          <button
            type="button"
            className={styles.addBtn}
            onClick={addInstallation}
          >
            {t('add_installation')}
          </button>
        )}
      </div>

      <div className={styles.installationsList}>
        {fields.map((field, index) => (
          <div key={field.id} className={styles.installationItem}>
            <div className={styles.installationHeader}>
              <h5>
                {t('installation')} #{index + 1}
              </h5>
              {isEditing && (
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={() => removeInstallation(index)}
                >
                  {t('remove')}
                </button>
              )}
            </div>
            <div className={styles.installationFields}>
              <TextInput
                {...rest}
                control={control}
                required
                name={`installations.${index}.name`}
                label={t('installation_name')}
              />
              <TextInput
                {...rest}
                control={control}
                required
                name={`installations.${index}.price_without_vat`}
                type="number"
                isCurrency
                label={t('installation_price_without_vat')}
                min={0}
              />
              <DropDownPicker
                {...rest}
                control={control}
                required
                name={`installations.${index}.installation_type`}
                label={t('installation_type')}
                options={installationTypeOptions}
              />
              <div className={styles.checkboxWrapper}>
                <TextInput
                  {...rest}
                  control={control}
                  name={`installations.${index}.is_customer_paid`}
                  type="checkbox"
                  label={t('installation_customer_paid')}
                />
              </div>
            </div>
          </div>
        ))}

        {fields.length === 0 && (
          <div className={styles.noInstallations}>
            <p>{t('no_installations_click_add')}</p>
          </div>
        )}
      </div>

      <div className={styles.modalActions}>
        <button type="button" className={styles.cancelBtn} onClick={onClose}>
          {t('close')}
        </button>
      </div>
    </div>
  )
}

export default InstallationsModal
